import { useQuery } from '@tanstack/react-query';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedSearch } from '@/hooks/useDebouncedValue';
import { listResource } from '@/lib/api';
import { LearningSessionTimeTable, type LearningSessionTimeRow } from './LearningSessionTimeTable';

export function LearningSessionTimePage() {
  const { value: search, setValue: setSearch, debounced, page, setPage } = useDebouncedSearch();
  const appliedSearch = debounced.trim();

  const { data, isLoading, error } = useQuery({
    queryKey: ['learning-session-student-time', appliedSearch, page],
    queryFn: () =>
      listResource<LearningSessionTimeRow>('/admin/learning-sessions/student-time', {
        page,
        ...(appliedSearch ? { search: appliedSearch } : {}),
      }),
  });

  const records = data?.data ?? [];
  const meta = data?.meta;
  const perPage = meta?.perPage ?? 30;
  const total = meta?.total ?? records.length;
  const lastPage = meta?.lastPage ?? 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);
  const hasSearch = search.trim().length > 0;

  function clearFilters() {
    setSearch('');
  }

  return (
    <div className="lms-learning-session-time lms-site-admin-hub-page font-sans">
      <header className="flex items-start gap-4">
        <HubBackLink to="/administracion?tab=plugins" label="Volver a Administración" />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Tiempo de conexión por alumno</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Tiempo activo registrado en la plataforma, agrupado por estudiante
          </p>
        </div>
      </header>

      <section className="lms-learning-session-time__filters">
        <form
          method="get"
          action="/progreso/tiempo-conexion"
          className="lms-learning-session-time__filters-grid"
          onSubmit={(event) => event.preventDefault()}
        >
          <div className="lms-learning-session-time__field lms-learning-session-time__field--search">
            <label htmlFor="session-time-search">Estudiante</label>
            <SearchInput
              id="session-time-search"
              name="search"
              value={search}
              onChange={setSearch}
              placeholder="Nombre o correo"
              aria-label="Buscar estudiante"
            />
          </div>
          <div className="lms-learning-session-time__actions">
            {hasSearch ? (
              <button type="button" className="lms-learning-session-time__btn lms-learning-session-time__btn--ghost" onClick={clearFilters}>
                Limpiar
              </button>
            ) : (
              <span className="lms-learning-session-time__btn lms-learning-session-time__btn--ghost lms-learning-session-time__btn--muted">
                Limpiar
              </span>
            )}
          </div>
        </form>
      </section>

      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      ) : null}

      <LearningSessionTimeTable
        records={records}
        isLoading={isLoading}
        page={page}
        lastPage={lastPage}
        total={total}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        onPageChange={setPage}
        courseTimeLabel="Tiempo total"
      />
    </div>
  );
}
